/**
 * AppLayout — Dynamic sidebar with grouped navigation
 *
 * Sidebar items are organized into 6 collapsible groups:
 *   Dashboard, Speed Review (top-level)
 *   Maintenance | Vendors & Dispatch | Properties & Assets | Intelligence | Settings
 *
 * Org users see items filtered by role:
 *   ORG_ADMIN / PM_MANAGER: all items
 *   MAINTENANCE: subset (WOs, vendors read, invoices, alerts, calendar, analytics)
 *   VIEWER: Dashboard, Work Orders, Properties, Analytics (read-only)
 *
 * Ops users see: Command Center only + active org access banner
 */

import { lazy, Suspense, useState, useEffect, useRef, useCallback, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { Layout, Menu, Typography, Avatar, Dropdown, Space, Alert, Button, Drawer, Grid, Badge } from 'antd';
import type { MenuProps } from 'antd';
import {
  PhoneOutlined,
  DashboardOutlined,
  ToolOutlined,
  TeamOutlined,
  HomeOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HolderOutlined,
  ThunderboltOutlined,
  BookOutlined,
  CalendarOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  DisconnectOutlined,
  OrderedListOutlined,
  DollarOutlined,
  AlertOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  IdcardOutlined,
  AuditOutlined,
  ShopOutlined,
  BulbOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  StarOutlined,
  ContainerOutlined,
  PieChartOutlined,
  SolutionOutlined,
  DownOutlined,
  CheckOutlined,
  TagsOutlined,
  KeyOutlined,
  InboxOutlined,
  MailOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import RouteFallback from './RouteFallback';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../i18n/LocaleProvider';
import { languageMenuItem } from './LanguageSwitcher';
import { typeScaleMenuItem } from './TypeScaleSwitcher';
import { useTypeScale } from '../theme/TypeScaleProvider';
import type { SupportedLocale } from '../i18n';
import { LOCALE_EXPLICIT_KEY } from '../i18n';
import AppDownloadModal from './AppDownloadModal';
// The floating assistant is not needed to paint the page — load it beside the page, not inside the entry chunk.
const PMChatWidget = lazy(() => import('./PMChatWidget'));
import { setPmChatOpen } from '../lib/pmChatOpen';
import Sparkle from './icons/Sparkle';
import { StatusChip } from './StatusChip';
import { useQueueSummary } from '../hooks/useQueueSummary';
import { useMakeReadySummary } from '../hooks/useMakeReadySummary';
import { useNoticeSummary } from '../hooks/useNoticeSummary';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import {
  ACCOUNTING_NAV_KEYS,
  accountingNavSelectedKey,
  accountingRedirectFor,
  canSeeAccountingBillsPage,
  isAccountingAllowedPath,
} from '../lib/accountingSurface';
import {
  LEASING_NAV_KEYS,
  isLeasingAllowedPath,
  isLeasingSeat,
} from '../lib/leasingSurface';

const { Sider, Content, Header } = Layout;
const { Text } = Typography;

/**
 * Desktop sidebar width. The Sider is `position: fixed` (index.css), so the
 * content's left margin has to match — both read `--sider-w`, set from this
 * value, so the two can never drift apart. User-resizable on desktop; icons-
 * only mode uses SIDER_COLLAPSED_W.
 */
const SIDER_DEFAULT_W = 248;
const SIDER_MIN_W = 200;
const SIDER_MAX_W = 360;
const SIDER_COLLAPSED_W = 64;
/** Drag below this → snap to icons-only. */
const SIDER_SNAP_W = 100;
const SIDER_WIDTH_LS_KEY = 'swiftops.siderWidth';
const SIDER_COLLAPSED_LS_KEY = 'swiftops.siderCollapsed';

function readStoredSiderWidth(): number {
  try {
    const n = Number(localStorage.getItem(SIDER_WIDTH_LS_KEY));
    if (Number.isFinite(n) && n >= SIDER_MIN_W && n <= SIDER_MAX_W) return Math.round(n);
  } catch { /* private mode */ }
  return SIDER_DEFAULT_W;
}

function readStoredSiderCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDER_COLLAPSED_LS_KEY) === '1';
  } catch { /* private mode */ }
  return false;
}

/** Nested nav-item indent. Below antd's 24px default to buy label width. */
const MENU_INLINE_INDENT = 16;

interface NavItem {
  key: string;
  icon: React.ReactNode;
  /** i18n key under the `common` namespace (e.g. `nav.workOrders`). */
  labelKey: string;
  /**
   * Capability id (spec 113 W1) that makes this item visible — shown when the
   * user `can(capability)`. Omitted = visible to every org role (ALL_PORTAL
   * view surfaces: dashboard, my-queue, work-orders, calendar, vendors,
   * properties). Nav visibility follows the section's VIEW capability, so a
   * read-only role (VIEWER) sees every section it can read (D-02); action-only
   * surfaces (speed-review, estimates) use the WO_WRITERS capability.
   */
  capability?: string;
  /**
   * The item is the accounting seat's own workspace — shown only to
   * ACCOUNTING (and ops-with-access). A capability can't express this: the
   * catalog's invariant makes admin the monotonic top of every capability,
   * and this is an audience rule, not a permission (see
   * `canSeeAccountingBillsPage` in ../lib/accountingSurface).
   */
  accountingSeatOnly?: boolean;
}

interface NavGroup {
  groupKey: string;
  icon: React.ReactNode;
  /** i18n key under the `common` namespace (e.g. `navGroups.maintenance`). */
  labelKey: string;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'groupKey' in entry;
}

/**
 * Build the org nav structure.
 *
 * Always (every environment): Templates lives in the Maintenance group (not Properties), and
 * the Inspections group is labelled "Inspections".
 * Vendor Batches is hidden in every environment — its route still exists in App.tsx;
 * only the nav entry is suppressed.
 */
function buildOrgNavStructure(): NavEntry[] {
  return [
    // Dashboard (= reports) moved to /dashboard — `/` is My Queue (PO item 9).
    { key: '/dashboard', icon: <DashboardOutlined />, labelKey: 'nav.dashboard' },
    // My Queue — THE unified work screen (spec 110): one ranked feed (My board
    // ⇄ Org-wide, which folds in the retired Alerts page). ALWAYS visible for
    // every org role (the spec-38 self-hiding rule is removed); carries the red
    // "needs my attention" badge from /queue/summary.
    { key: '/my-queue', icon: <InboxOutlined />, labelKey: 'nav.myQueue' },
    // The mailbox Inbox (spec 170 §3.5) — directly under My queue, and an
    // ENVELOPE: My queue owns the tray glyph, and two inbox icons is exactly
    // the confusion the spec forbids. Its badge is the UNREAD count and
    // nothing else — never a pile of work waiting for you.
    // `inbox:title` is a cross-namespace key; the nav's translator is `common`.
    { key: '/inbox', icon: <MailOutlined />, labelKey: 'inbox:title' },
    // Speed Review — WO triage/dispatch ACTION surface → writers only.
    { key: '/speed-review', icon: <ThunderboltOutlined />, labelKey: 'nav.speedReview', capability: 'work_orders.operate' },
    // Alerts nav item removed (spec 110 D3) — folded into My Queue → Org-wide.
    // Make-Ready — promoted top-level so unit-turn automation
    // is a first-class destination, not buried in a group (PO ask). Carries the
    // live "active turns" badge (attached in buildOrgMenuItems). VIEWER gets the
    // read-only list.
    { key: '/make-ready', icon: <KeyOutlined />, labelKey: 'nav.makeReady', capability: 'make_ready.view' },
    /**
     * All bills — promoted top-level, beside My Queue (PO, 2026-08-05: *"I asked
     * to move all bills one level up, to the same level as my queue"*).
     *
     * It sat inside the Maintenance group next to `/invoices`, which reads as
     * though it were a second copy of that list. It is not: `/invoices` is the
     * PM's APPROVAL queue, this is the whole of accounting's own work, and for
     * the ACCOUNTING seat it is one of only two destinations they have. Same
     * reasoning that promoted Make-Ready: a first-class job does not live
     * buried in somebody else's group.
     *
     * ★ ACCOUNTING SEAT ONLY (PO, 2026-08-12: *"visible only to accounting
     * department, hide it for others"*) — Admin/PM no longer get this nav
     * item. The capability stays on the entry's route guards elsewhere; the
     * audience rule lives in `canSeeAccountingBillsPage`.
     */
    { key: '/accounting', icon: <BankOutlined />, labelKey: 'nav.accounting', capability: 'accounting.bills.view', accountingSeatOnly: true },

    {
      groupKey: 'operations',
      icon: <ContainerOutlined />,
      labelKey: 'navGroups.maintenance',
      children: [
        { key: '/work-orders', icon: <ToolOutlined />, labelKey: 'nav.workOrders' },
        // Calendar (portfolio scheduling grid) is retired from the nav for ALL
        // roles (PO 2026-07-18) — the My Queue calendar view supersedes it. The
        // /calendar route still exists (reachable by URL), just not surfaced here.
        // D-02: invoices.view = ALL_PORTAL → VIEWER now sees the list read-only.
        { key: '/invoices', icon: <DollarOutlined />, labelKey: 'nav.invoices', capability: 'invoices.view' },
        // All bills (accounting's own register) moved OUT of this group and up
        // to the top level — see the comment on it above.
        // Estimates has no viewer-read capability → the WO_WRITERS request gate.
        { key: '/estimates', icon: <FileTextOutlined />, labelKey: 'nav.estimates', capability: 'estimates.request' },
        // Work-order Templates lives in Maintenance in every environment (moved out of
        // Properties). NOT the same entry as the
        // Inspections group's `/inspections/templates`, which stays visible everywhere.
        { key: '/templates', icon: <FileTextOutlined />, labelKey: 'nav.templates', capability: 'wo_templates.manage' },
        // Vendor Batches hidden everywhere pending clearer UX — route still lives at /vendor-batches.
        // Make-Ready moved to the Resident Moves group (spec 99 W1, R25 + DQ1).
      ],
    },

    {
      groupKey: 'inspections',
      icon: <AuditOutlined />,
      labelKey: 'navGroups.inspections',
      children: [
        // The LIST is `inspections.view` (ALL_PORTAL) — the spec 113 W2 deferral is
        // closed. MAINTENANCE conducts the walks (`inspections.conduct`) and had no
        // way to reach the list they conduct them from; the backend's GET endpoints
        // were already open to any org user, so only this nav entry and the route
        // guard were blocking them. The page hides every ADMIN_PM write action
        // (create / edit / assign / vacate capture) behind `inspections.configure`
        // — see InspectionsPage's `canConfigure`. Templates / Categories / Recurring
        // below stay `inspections.configure`: those are genuinely config surfaces.
        { key: '/inspections', icon: <AuditOutlined />, labelKey: 'nav.inspections', capability: 'inspections.view' },
        { key: '/inspections/templates', icon: <FileTextOutlined />, labelKey: 'nav.templates', capability: 'inspections.configure' },
        { key: '/inspections/categories', icon: <TagsOutlined />, labelKey: 'nav.categories', capability: 'inspections.configure' },
        { key: '/action-items', icon: <AlertOutlined />, labelKey: 'nav.actionItems', capability: 'inspections.action_items' },
        { key: '/recurring-inspections', icon: <CalendarOutlined />, labelKey: 'nav.recurring', capability: 'inspections.configure' },
        // Compliance Registry. D-02: compliance.view = ALL_PORTAL.
        { key: '/compliance', icon: <SafetyCertificateOutlined />, labelKey: 'nav.complianceRegistry', capability: 'compliance.view' },
      ],
    },

    // Resident Moves — prod-visible since spec 99 W1 (DQ1: un-hiding the group
    // so Make-Ready ships means Notices/Dispositions go prod-visible too —
    // flagged to the PO, deliberate).
    {
      groupKey: 'resident-moves',
      icon: <LogoutOutlined />,
      labelKey: 'navGroups.residentMoves',
      children: [
        // D-02: notices.view / dispositions.view = ALL_PORTAL → read-only for VIEWER.
        { key: '/notices', icon: <LogoutOutlined />, labelKey: 'nav.noticesToVacate', capability: 'notices.view' },
        // Make-Ready promoted to a top-level nav item (see after Alerts above).
        { key: '/dispositions', icon: <DollarOutlined />, labelKey: 'nav.moveOutDispositions', capability: 'dispositions.view' },
      ],
    },

    {
      groupKey: 'vendors',
      icon: <TeamOutlined />,
      labelKey: 'navGroups.vendorsDispatch',
      children: [
        { key: '/vendors', icon: <TeamOutlined />, labelKey: 'nav.vendors' },
        { key: '/vendor-preferences', icon: <OrderedListOutlined />, labelKey: 'nav.vendorPreferences', capability: 'dispatch.manage' },
        { key: '/dispatch-rules', icon: <ThunderboltOutlined />, labelKey: 'nav.dispatchRules', capability: 'dispatch.manage' },
        // Vendor Portal preview
        { key: '/vendor-portal-preview', icon: <ShopOutlined />, labelKey: 'nav.vendorPortal', capability: 'vendors.manage' },
      ],
    },

    {
      groupKey: 'properties',
      icon: <HomeOutlined />,
      labelKey: 'navGroups.propertiesAssets',
      children: [
        { key: '/properties', icon: <HomeOutlined />, labelKey: 'nav.properties' },
        { key: '/assets', icon: <DatabaseOutlined />, labelKey: 'nav.assetRegistry', capability: 'assets.manage' },
        // Owners. D-02: owners.view = ALL_PORTAL (read-only).
        // NOTE (2026-07-16): the owner flow is not yet implemented — owner APPROVAL and
        // owner MESSAGING are TEMPORARILY PARKED (see automation-pipeline.service.ts +
        // specification/12-owner-approvals.md). Shown in every environment; the page is
        // read-only.
        { key: '/owners', icon: <IdcardOutlined />, labelKey: 'nav.owners', capability: 'owners.view' },
        // Templates moved to the Maintenance group (all environments).
      ],
    },

    {
      groupKey: 'intelligence',
      icon: <PieChartOutlined />,
      labelKey: 'navGroups.intelligence',
      children: [
        // Spec 179 D2 (2026-09-15): the Analytics menu item is gone — its tables
        // are sub-pages under /dashboard/…, each opened from a Dashboard section.
        // Surveys + Coordinator coach have no dedicated capability; both are
        // Intelligence-group ADMIN_PM surfaces → reuse the analytics.org gate.
        { key: '/surveys', icon: <StarOutlined />, labelKey: 'nav.surveys', capability: 'analytics.org' },
        { key: '/ai-knowledge', icon: <BulbOutlined />, labelKey: 'nav.aiKnowledgeCenter', capability: 'ai_memory.org' },
        // Coordinator coach
        { key: '/coordinator-coach', icon: <SolutionOutlined />, labelKey: 'nav.coordinatorCoach', capability: 'analytics.org' },
        // PO 2026-09-01 (reverses D-02's nav widening): the KB nav item shows
        // only for authors. Reads stay open server-side (kb.view = ALL_PORTAL),
        // so direct links and AI-cited articles still work for every role.
        { key: '/knowledge-base', icon: <BookOutlined />, labelKey: 'nav.knowledgeBase', capability: 'kb.manage' },
      ],
    },

    {
      groupKey: 'settings',
      icon: <SettingOutlined />,
      labelKey: 'navGroups.settings',
      children: [
        { key: '/team', icon: <UserOutlined />, labelKey: 'nav.team', capability: 'team.manage' },
        // Repair/replace rules + NTE Config
        { key: '/rules/repair-replace', icon: <ToolOutlined />, labelKey: 'nav.repairReplaceRules', capability: 'dispatch.manage' },
        { key: '/nte-config', icon: <DollarOutlined />, labelKey: 'nav.nteConfig', capability: 'dispatch.manage' },
        { key: '/sla', icon: <ClockCircleOutlined />, labelKey: 'nav.slaFollowUps', capability: 'sla.manage' },
        { key: '/on-call', icon: <PhoneOutlined />, labelKey: 'nav.onCall', capability: 'oncall.view' },
        // Automation & Scheduling — retired from the side nav (route kept for
        // deep links / bookmarks). Was prod-hidden only; PO asked to hide everywhere.
        { key: '/integrations', icon: <LinkOutlined />, labelKey: 'nav.integrations', capability: 'integrations.manage' },
        { key: '/org-settings', icon: <SettingOutlined />, labelKey: 'nav.orgSettings', capability: 'settings.view' },
        { key: '/audit-log', icon: <FileTextOutlined />, labelKey: 'nav.auditLog', capability: 'audit.view' },
      ],
    },
  ];
}

const orgNavStructure: NavEntry[] = buildOrgNavStructure();

/**
 * Build Ant Design Menu items from the nav structure, filtered by role.
 * Groups with zero visible children are removed entirely.
 */
/** Minimal translator signature — resolves a `common`-namespace i18n key. */
type TFn = (key: string) => string;

/**
 * Spec 136 — Accounting nav is My Queue only. Disposition / WO / inspection
 * detail pages open from a queue deep-link (see `isAccountingAllowedPath`),
 * never as sidebar destinations.
 */
// ACCOUNTING_NAV_KEYS imported from ../lib/accountingSurface

function buildOrgMenuItems(
  t: TFn,
  // Capability predicate (spec 113 W1): a nav item shows when the user holds its
  // `capability` (or the item has none). For ops-with-access `can` returns true
  // for everything (mirrors the backend ops-bypass), so ops still sees all nav.
  can: (capability: string) => boolean,
  // My Queue's red badge = my attention count (spec 110 §3.4:
  // attention.critical + attention.high from /queue/summary). Hidden at 0.
  myAttentionCount = 0,
  // Live badge counts for nav entries (group children or top-level), keyed by
  // route (spec 99: the Make-Ready entry carries its active-turn count). At 0
  // the badge is hidden.
  childBadges: Record<string, number> = {},
  /** Spec 136 — when ACCOUNTING, sidebar = My Queue only. */
  role?: string,
  /** Ops-with-access sees all nav (mirrors the backend ops-bypass). */
  opsWithAccess = false,
  /**
   * The leasing seat — sidebar = My Queue only, same rule as ACCOUNTING but
   * keyed on `primaryDepartment` because there is no LEASING role to key on
   * (see `../lib/leasingSurface`). Computed by the caller so this stays a pure
   * function of what it is handed.
   */
  leasingTrim = false,
): MenuProps['items'] {
  const items: MenuProps['items'] = [];
  const accountingTrim = role === 'ACCOUNTING';
  const seatOnlyVisible = canSeeAccountingBillsPage(role, opsWithAccess);

  for (const entry of orgNavStructure) {
    if (isGroup(entry)) {
      const visibleChildren = entry.children.filter(
        (child) =>
          (!child.capability || can(child.capability)) &&
          (!child.accountingSeatOnly || seatOnlyVisible) &&
          (!accountingTrim || ACCOUNTING_NAV_KEYS.has(child.key)) &&
          (!leasingTrim || LEASING_NAV_KEYS.has(child.key)),
      );
      if (visibleChildren.length === 0) continue;

      items.push({
        key: entry.groupKey,
        icon: entry.icon,
        label: t(entry.labelKey),
        children: visibleChildren.map((child) => {
          const badge = childBadges[child.key] ?? 0;
          const resolved = t(child.labelKey);
          return {
            key: child.key,
            icon: child.icon,
            label:
              badge > 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {resolved}
                  <Badge
                    count={badge}
                    size="small"
                    overflowCount={99}
                    style={{ backgroundColor: 'var(--brand)' }}
                  />
                </span>
              ) : (
                resolved
              ),
          };
        }),
      });
    } else {
      if (entry.capability && !can(entry.capability)) continue;
      if (entry.accountingSeatOnly && !seatOnlyVisible) continue;
      if (accountingTrim && !ACCOUNTING_NAV_KEYS.has(entry.key)) continue;
      if (leasingTrim && !LEASING_NAV_KEYS.has(entry.key)) continue;
      // My Queue carries a live RED count of items needing my attention
      // (spec 110 §3.4). Other top-level items (Make-Ready) carry their purple
      // childBadge count. Both hidden at 0. My Queue is ALWAYS visible — the
      // old spec-38 self-hiding rule is removed.
      const resolved = t(entry.labelKey);
      const topBadge = childBadges[entry.key] ?? 0;
      let label: ReactNode = resolved;
      if (entry.key === '/my-queue' && myAttentionCount > 0) {
        label = (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {resolved}
            <Badge count={myAttentionCount} size="small" overflowCount={99} />
          </span>
        );
      } else if (topBadge > 0) {
        label = (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {resolved}
            <Badge
              count={topBadge}
              size="small"
              overflowCount={99}
              style={{ backgroundColor: 'var(--brand)' }}
            />
          </span>
        );
      }
      items.push({
        key: entry.key,
        icon: entry.icon,
        label,
      });
    }
  }

  return items;
}

/**
 * Find which group key contains a given route path.
 */
function findGroupForPath(pathname: string): string | null {
  for (const entry of orgNavStructure) {
    if (!isGroup(entry)) continue;
    for (const child of entry.children) {
      if (pathname === child.key || (child.key !== '/' && pathname.startsWith(child.key))) {
        return entry.groupKey;
      }
    }
  }
  return null;
}

/**
 * Collect all leaf-level route keys (for selectedKey matching).
 */
function getAllRouteKeys(): string[] {
  const keys: string[] = [];
  for (const entry of orgNavStructure) {
    if (isGroup(entry)) {
      for (const child of entry.children) keys.push(child.key);
    } else {
      keys.push(entry.key);
    }
  }
  return keys;
}

/** Humanize an org-user role for the switcher's per-org tag. */
function roleLabel(t: TFn, role: string): string {
  switch (role) {
    case 'ORG_ADMIN': return t('role.admin');
    case 'PM_MANAGER': return t('role.manager');
    case 'MAINTENANCE': return t('role.maintenance');
    case 'ACCOUNTING': return t('role.accounting');
    case 'VIEWER': return t('role.viewer');
    default: return role;
  }
}

function buildOpsMenuItems(t: TFn): MenuProps['items'] {
  return [
    { key: '/admin/orgs', icon: <BankOutlined />, label: t('nav.organizations') },
    { key: '/admin/access-tokens', icon: <SafetyCertificateOutlined />, label: t('nav.accessTokens') },
    { key: '/admin/ops-team', icon: <TeamOutlined />, label: t('nav.opsTeam') },
  ];
}

function getOpsWithAccessMenuItems(
  t: TFn,
  can: (capability: string) => boolean,
  myAttentionCount = 0,
  childBadges: Record<string, number> = {},
  role?: string,
): MenuProps['items'] {
  return [
    ...buildOpsMenuItems(t)!,
    { type: 'divider' as const },
    ...buildOrgMenuItems(t, can, myAttentionCount, childBadges, role, true)!,
  ];
}

export default function AppLayout() {
  const { user, logout, isOpsUser, opsOrgAccess, setOpsOrgAccess, organizations, switchOrg, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { locale, setLocale } = useLocale();
  const { typeScale, setTypeScale } = useTypeScale();


  // Account-locale adoption (spec 91 §6): if the signed-in user carries a saved
  // `locale` AND this device has no EXPLICIT switcher pick, adopt the account
  // locale. Gated on the explicit-pick flag (not the mere presence of the locale
  // value) so a returning/different user on a shared device still adopts their
  // own account locale — while a real explicit pick always wins. Adoption itself
  // calls setLocale with `explicit: false`, so it persists the value without
  // marking the device as explicitly chosen. Written defensively — `user.locale`
  // may not exist until Wave 0-A merges.
  useEffect(() => {
    const accountLocale = (user as { locale?: unknown } | null)?.locale;
    if (accountLocale !== 'en' && accountLocale !== 'es') return;
    let hasExplicitPick = false;
    try {
      hasExplicitPick = localStorage.getItem(LOCALE_EXPLICIT_KEY) === '1';
    } catch { /* ignore */ }
    if (hasExplicitPick) return;
    if (accountLocale !== locale) {
      setLocale(accountLocale as SupportedLocale, { explicit: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ONE summary poll for the whole shell (spec 110 §7.2 — supersedes the old
  // useAlertSummary + useHandoffQueueCounters pair). My Queue's red badge =
  // items needing MY attention (critical + high, me-scope).
  const { summary: queueSummary } = useQueueSummary();
  const myAttention = queueSummary.attention.critical + queueSummary.attention.high;
  // Make-Ready nav badge = live ACTIVE turn count (spec 99). Same query the
  // list page uses, so the badge can never disagree with the list.
  const { counts: makeReadyCounts } = useMakeReadySummary();
  // Notices nav badge = live PENDING_CONFIRMATION count (A1). Same query source
  // as the alert count, so the badge never disagrees with the Notices list.
  const { counts: noticeCounts } = useNoticeSummary();
  // Inbox nav badge = UNREAD rows, from the same function that produces the
  // page's own counts (spec 170 I11), so the badge can never disagree with the
  // five summary cards. It clears when you look — it never grows into a pile.
  const { data: inboxUnread } = useQuery({
    queryKey: ['inbox-counts'],
    queryFn: async (): Promise<number> => {
      const res = await api.get('/mailbox/inbox/counts');
      return res.data?.data?.unread ?? 0;
    },
    enabled: !isOpsUser || !!opsOrgAccess,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const childBadges: Record<string, number> = {
    '/make-ready': makeReadyCounts.active,
    '/notices': noticeCounts.pendingConfirmation,
    '/inbox': inboxUnread ?? 0,
  };

  // The leasing seat — one surface, their queue (PO, 2026-08-12: *"We hide all
  // pages. Keep My Board"*). Keyed on the DEPARTMENT, not a role, and never on
  // an admin; the whole rule lives in `../lib/leasingSurface`.
  const leasingSeat = isLeasingSeat(user);

  const menuItems = isOpsUser
    ? (opsOrgAccess ? getOpsWithAccessMenuItems(t, can, myAttention, childBadges, user?.role) : buildOpsMenuItems(t))
    : buildOrgMenuItems(t, can, myAttention, childBadges, user?.role, false, leasingSeat);

  /**
   * ★ DECIDED HERE, RENDERED AT THE BOTTOM — and that placement is the fix.
   *
   * Both seat guards used to `return <Navigate/>` right here, ABOVE ~20 more
   * hooks (`useState` at 639 onward). `AppLayout` is a layout route, so React
   * Router never remounts it between child navigations — the fiber persists.
   * A seat user who rendered an ALLOWED path (all ~30 hooks) and then hit a
   * blocked one returned after 9, and React threw "Rendered fewer hooks than
   * expected" (or "more", on the reverse order). There is no error boundary
   * anywhere in `frontend/src`, so the root unmounted: a white screen instead
   * of a redirect, recoverable only by a full reload.
   *
   * Reproduced live against this repo's React 18.3.1 + react-router 6.30.3.
   * It is ONE CLICK from the leasing seat's only page — My Queue's "Visits
   * today" card navigates to `/calendar`, which is on neither allow-list.
   *
   * This was NOT introduced by the leasing seat: the accounting branch has had
   * the identical shape since spec 136 and crashes today the same way. Both are
   * hoisted into one decision here and one render at the end, so the component
   * always runs every hook before it returns anything.
   */
  const seatRedirect: string | null = (() => {
    // Spec 136 — Accounting may not browse the app. Sidebar = My Queue; detail
    // pages only via queue deep-links. Typed / bookmarked list URLs bounce home.
    if (
      user?.role === 'ACCOUNTING' &&
      !isAccountingAllowedPath(location.pathname, location.search)
    ) {
      // …except where they have a page of their own for the same thing: a queue
      // task on a bill still deep-links to `/work-orders/:id`, so that one is
      // re-pointed at the accounting task page rather than dumped at My Queue.
      return accountingRedirectFor(location.pathname) ?? '/my-queue';
    }
    // Same rule for leasing, with no re-pointing: their queue task already opens
    // the leasing page, and the inspection / work order it links to are on the
    // allow-list, so there is no better destination than the queue itself.
    if (leasingSeat && !isLeasingAllowedPath(location.pathname, location.search)) {
      return '/my-queue';
    }
    return null;
  })();

  // Determine selected key from current path
  const allKeys = getAllRouteKeys();
  const selectedKey = leasingSeat
    ? '/my-queue'
    : user?.role === 'ACCOUNTING'
      ? accountingNavSelectedKey(location.pathname)
      : allKeys
          .filter((k) => k !== '/' && location.pathname.startsWith(k))
          .sort((a, b) => b.length - a.length)[0] || '/dashboard';

  // Auto-open the group containing the current route
  const activeGroup = findGroupForPath(location.pathname);
  const [openKeys, setOpenKeys] = useState<string[]>(activeGroup ? [activeGroup] : []);

  // Email auto-login banner is informational now that the link session is
  // fully capable (spec 102 as-built amendment 2026-07-15) — let staff dismiss
  // it for the tab session (sessionStorage: gone on close, shown once per new
  // tab). "Not you? Sign out" stays as the escape hatch; dismissing NEVER logs
  // the user out.
  const EMAIL_LINK_BANNER_DISMISSED_KEY = 'swiftops.emailLinkBannerDismissed';
  const [emailLinkBannerDismissed, setEmailLinkBannerDismissed] = useState(
    () => sessionStorage.getItem(EMAIL_LINK_BANNER_DISMISSED_KEY) === '1',
  );
  const dismissEmailLinkBanner = () => {
    sessionStorage.setItem(EMAIL_LINK_BANNER_DISMISSED_KEY, '1');
    setEmailLinkBannerDismissed(true);
  };

  useEffect(() => {
    const group = findGroupForPath(location.pathname);
    if (group && !openKeys.includes(group)) {
      setOpenKeys((prev) => [...prev, group]);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate(isOpsUser ? '/ops/login' : '/login');
  };

  const disconnectOrgAccess = () => {
    setOpsOrgAccess(null);
    navigate('/admin/orgs');
  };

  const userMenu = {
    items: [
      ...(isOpsUser ? [{ key: 'role', label: t('shell.roleLabel', { role: user?.role }), disabled: true }] : []),
      { key: 'settings', icon: <UserOutlined />, label: t('actions.profile') },
      languageMenuItem(t, locale),
      typeScaleMenuItem(t, typeScale),
      ...(opsOrgAccess ? [
        { type: 'divider' as const },
        { key: 'disconnect', icon: <DisconnectOutlined />, label: t('shell.disconnectFrom', { name: opsOrgAccess.orgName }), danger: true },
      ] : []),
      { type: 'divider' as const },
      { key: 'logout', icon: <LogoutOutlined />, label: t('actions.signOut'), danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'settings') navigate('/account');
      if (key === 'logout') handleLogout();
      if (key === 'disconnect') disconnectOrgAccess();
      if (key === 'lang:en') setLocale('en');
      if (key === 'lang:es') setLocale('es');
      if (key === 'scale:proposed') setTypeScale('proposed');
      if (key === 'scale:comfortable') setTypeScale('comfortable');
    },
  };

  const subtitle = isOpsUser
    ? (opsOrgAccess ? t('shell.viewing', { name: opsOrgAccess.orgName }) : t('shell.commandCenter'))
    : (user?.type === 'org' ? user.orgName : '');

  const screens = Grid.useBreakpoint();
  // `!screens.md` is true on the first paint (md is undefined) — treat as desktop
  // until the breakpoint explicitly resolves below md.
  const isMobile = screens.md === false;
  // Collapse the persistent left sidebar into a hamburger Drawer below `lg`
  // (992px) — tablets, split-screen, small laptops. This frees the ~248px the
  // Sider ate so the page content gets the full width (the old md/768 threshold
  // left the sidebar eating half the screen on those widths). `isMobile` (md)
  // still gates the phone-only chrome (tighter padding, hidden name). Keep in
  // lockstep with the `@media (min-width: 992px)` sidebar rules in index.css.
  const navCollapsed = screens.lg === false;
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  // Desktop sider: user-resizable width + icons-only collapse (persisted).
  // Mobile drawer always uses the full labelled sidebar — these states only
  // apply when `!navCollapsed` (inline Sider ≥ lg).
  const [siderWidth, setSiderWidth] = useState(readStoredSiderWidth);
  const [siderIconsOnly, setSiderIconsOnly] = useState(readStoredSiderCollapsed);
  const [siderDragWidth, setSiderDragWidth] = useState<number | null>(null);
  const [siderResizing, setSiderResizing] = useState(false);
  const siderDragRef = useRef<{
    startX: number;
    startW: number;
  } | null>(null);
  const siderDragWidthRef = useRef<number | null>(null);
  const siderResizeCleanupRef = useRef<(() => void) | null>(null);
  // Dashboard: the white app header auto-hides once you scroll down (true), and
  // slides back when you return to the top (false). Desktop only.
  const [headerHiddenOnScroll, setHeaderHiddenOnScroll] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const effectiveSiderW = navCollapsed
    ? SIDER_DEFAULT_W
    : siderIconsOnly
      ? SIDER_COLLAPSED_W
      : (siderDragWidth ?? siderWidth);

  const abortSiderResize = useCallback(() => {
    siderResizeCleanupRef.current?.();
    siderResizeCleanupRef.current = null;
    siderDragRef.current = null;
    siderDragWidthRef.current = null;
    setSiderDragWidth(null);
    setSiderResizing(false);
  }, []);

  useEffect(() => {
    if (navCollapsed) return;
    try {
      localStorage.setItem(SIDER_WIDTH_LS_KEY, String(siderWidth));
      localStorage.setItem(SIDER_COLLAPSED_LS_KEY, siderIconsOnly ? '1' : '0');
    } catch { /* private mode */ }
  }, [navCollapsed, siderWidth, siderIconsOnly]);

  // Drop mid-drag if the viewport falls below `lg` (handle unmounts) or the
  // shell unmounts — otherwise `app-layout--resizing` sticks (cursor + no select).
  useEffect(() => {
    if (navCollapsed) abortSiderResize();
  }, [navCollapsed, abortSiderResize]);

  useEffect(() => () => abortSiderResize(), [abortSiderResize]);

  const toggleSiderIcons = useCallback(() => {
    setSiderIconsOnly((prev) => !prev);
    siderDragWidthRef.current = null;
    setSiderDragWidth(null);
  }, []);

  const onSiderResizePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    // Tear down any prior gesture (rare: missed pointerup).
    siderResizeCleanupRef.current?.();
    e.currentTarget.setPointerCapture(e.pointerId);
    siderDragRef.current = {
      startX: e.clientX,
      startW: siderIconsOnly ? SIDER_COLLAPSED_W : (siderDragWidth ?? siderWidth),
    };
    setSiderResizing(true);

    const onMove = (ev: PointerEvent) => {
      const drag = siderDragRef.current;
      if (!drag) return;
      const next = drag.startW + (ev.clientX - drag.startX);
      if (next < SIDER_SNAP_W) {
        setSiderIconsOnly(true);
        siderDragWidthRef.current = null;
        setSiderDragWidth(null);
        // Re-anchor at the cursor so expanding again in the same gesture is
        // relative to collapsed + current X (not the pre-snap baseline).
        siderDragRef.current = { startX: ev.clientX, startW: SIDER_COLLAPSED_W };
        return;
      }
      // Live width never dips below MIN — avoids a jump on release when the
      // cursor sat in the SNAP…MIN dead zone (stick at MIN until snap).
      const preview = Math.min(SIDER_MAX_W, Math.max(SIDER_MIN_W, Math.round(next)));
      setSiderIconsOnly(false);
      siderDragWidthRef.current = preview;
      setSiderDragWidth(preview);
    };

    const onUp = () => {
      siderResizeCleanupRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (!siderDragRef.current) return;
      siderDragRef.current = null;
      setSiderResizing(false);
      const preview = siderDragWidthRef.current;
      siderDragWidthRef.current = null;
      setSiderDragWidth(null);
      if (preview != null) {
        setSiderWidth(Math.min(SIDER_MAX_W, Math.max(SIDER_MIN_W, preview)));
      }
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    siderResizeCleanupRef.current = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [siderIconsOnly, siderDragWidth, siderWidth]);

  const onContentScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const top = el.scrollTop;
    const overflow = el.scrollHeight - el.clientHeight;
    setHeaderHiddenOnScroll((prev) => {
      const HEADER_H = 64;
      if (!prev) {
        return top > 80 && overflow > HEADER_H + 100 ? true : prev;
      }
      return top < 8 ? false : prev;
    });
  }, []);

  // Full-bleed routes own their entire content area (sticky header + fixed
  // footer + internal padding). The default Content padding would inset such
  // a page, which (a) leaves gutters and (b) pushes its sticky header's
  // containing block down by the padding, so the header pins below the
  // scrollport and scrolled content bleeds through the gap above it. The
  // inspection walk screen is one such page.
  const isFullBleedRoute = /^\/inspections\/[^/]+\/walk\/?$/.test(
    location.pathname,
  );

  // The Work-Order detail page renders its OWN sticky header (back / WO title /
  // actions / progress). It joins the auto-hide-on-scroll routes (below): the
  // generic app header shows at the top and slides away on scroll, so the WO
  // header pins flush once you scroll — no permanent double header (PO 2026-07-18).
  // `new` is excluded so the create page keeps the normal (non-sticky) header.
  const isWorkOrderDetail = /^\/work-orders\/(?!new$)[^/]+$/.test(location.pathname);

  // Dashboard keeps the white app header, but it AUTO-HIDES on scroll: visible
  // at the top, slides up + collapses to reclaim space once you scroll down, so
  // the page's own sticky "Dashboard + filters" bar is what stays pinned.
  // Desktop only — mobile keeps the hamburger header.
  const isDashboard = location.pathname === '/dashboard';
  // The Work Orders LIST uses the exact same scroll behavior as the dashboard:
  // its own sticky filter bar pins flush to the top, the app header auto-hides
  // on scroll to reclaim space, and the Content top padding is zeroed so no
  // strip of scrolled content bleeds through above the stuck bar. Detail/new
  // pages are excluded (detail renders its own header; new keeps the normal one).
  const isWorkOrdersList = location.pathname === '/work-orders';
  const isWorkOrderTemplates = location.pathname === '/templates';
  const isInspectionTemplates = location.pathname === '/inspections/templates';
  const isInspectionsList = location.pathname === '/inspections';
  const isInvoicesList = location.pathname === '/invoices';
  const isEstimatesList = location.pathname === '/estimates';
  const isVendorsList = location.pathname === '/vendors';
  const isActionItemsList = location.pathname === '/action-items';
  const isRecurringInspectionsList = location.pathname === '/recurring-inspections';
  const isComplianceRegistry = location.pathname === '/compliance';
  const isInspectionCategories = location.pathname === '/inspections/categories';
  // Make-Ready LIST only — the unit-folder detail keeps the normal header.
  const isMakeReadyList = location.pathname === '/make-ready';
  const isKnowledgeBaseList = location.pathname === '/knowledge-base';
  const isDispatchRulesList = location.pathname === '/dispatch-rules';
  const isNoticesList = location.pathname === '/notices';
  const isDispositionsList = location.pathname === '/dispositions';
  const isPropertiesList = location.pathname === '/properties';
  const isTeamList = location.pathname === '/team';
  const isSurveysList = location.pathname === '/surveys';
  const isAuditLogList = location.pathname === '/audit-log';
  // Routes whose own sticky top-bar should pin flush (header auto-hide on
  // scroll + zeroed Content top padding). Desktop only — mobile keeps the
  // hamburger header so the nav drawer stays reachable.
  // Auto-hide-on-scroll only when the sidebar is inline (≥lg). When the nav is
  // drawered, the app header hosts the hamburger, so it must stay put.
  // Routes whose page renders its OWN top-pinned sticky bar. These need the
  // Content's top padding zeroed so the sticky bar pins flush — a sticky element
  // pins to the padding box, so any top padding leaves a strip above it where
  // scrolled content shows through. This must hold even when the nav is drawered
  // (hamburger in the header) — otherwise that strip reappears on narrow screens.
  const isStickyBarRoute =
    isDashboard || isWorkOrdersList || isWorkOrderDetail || isWorkOrderTemplates || isInspectionTemplates || isInspectionsList || isInvoicesList || isEstimatesList || isVendorsList || isActionItemsList || isRecurringInspectionsList || isComplianceRegistry || isInspectionCategories || isMakeReadyList || isKnowledgeBaseList || isDispatchRulesList || isNoticesList || isDispositionsList || isPropertiesList || isTeamList || isSurveysList || isAuditLogList;
  // The header only AUTO-HIDES when the nav isn't drawered — when drawered, the
  // header hosts the hamburger, so it must stay put.
  const isAutoHideHeaderRoute = isStickyBarRoute && !navCollapsed;
  const collapseHeaderOnScroll = isAutoHideHeaderRoute && headerHiddenOnScroll;

  // Close mobile drawer on route change.
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  // Fresh list page → show the app header and pin scroll at the top. Without
  // this, navigating between two auto-hide routes while scrolled can leave the
  // header stuck collapsed on the new page.
  useEffect(() => {
    setHeaderHiddenOnScroll(false);
    const el = contentRef.current;
    if (el) el.scrollTop = 0;
  }, [location.pathname]);

  // Reset the auto-hide whenever we leave an auto-hide route (or hit mobile),
  // so the app header is never stuck collapsed on a normal page.
  useEffect(() => {
    if (!isAutoHideHeaderRoute) setHeaderHiddenOnScroll(false);
  }, [isAutoHideHeaderRoute]);

  // Passive listener on the content scroller (ref-backed — survives route swaps).
  useEffect(() => {
    if (!isAutoHideHeaderRoute) return;
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('scroll', onContentScroll, { passive: true });
    return () => el.removeEventListener('scroll', onContentScroll);
  }, [isAutoHideHeaderRoute, location.pathname, onContentScroll]);

  // Account switcher — only for org users who belong to more than one org.
  // Single-org users see no change (their org name already shows in the
  // top-right user menu). Renders a dropdown of the orgs this email can switch
  // into; picking one mints a fresh token for that org and reloads.
  const currentOrgName = user?.type === 'org' ? user.orgName : '';
  const orgSwitcher = user?.type === 'org' && organizations.length > 1 ? (
    <Dropdown
      trigger={['click']}
      menu={{
        items: organizations.map((o) => ({
          key: o.orgId,
          label: (
            <Space style={{ display: 'flex', width: '100%' }}>
              {o.isCurrent
                ? <CheckOutlined style={{ color: 'var(--brand)' }} />
                : <span style={{ display: 'inline-block', width: 14 }} />}
              <span style={{ flex: 1 }}>{o.orgName}</span>
              <StatusChip tone="type" label={roleLabel(t, o.role)} size="sm" />
            </Space>
          ),
        })),
        onClick: ({ key }) => {
          if (key !== user.orgId) void switchOrg(key);
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          background: '#fafafa',
          cursor: 'pointer',
        }}
      >
        <BankOutlined style={{ color: 'var(--brand)' }} />
        <Text strong ellipsis style={{ flex: 1, fontSize: 'var(--t-hint)' }}>{currentOrgName}</Text>
        <DownOutlined style={{ fontSize: 'var(--t-micro)', color: '#8c8c8c' }} />
      </div>
    </Dropdown>
  ) : null;

  const renderSidebar = (opts: { iconsOnly: boolean; showDesktopChrome: boolean }) => (
    <>
      <div className={`app-sider-brand${opts.iconsOnly ? ' app-sider-brand--icons' : ''}`}>
        <img
          src="/icon-192.png?v=swiftops-logo"
          alt="SwiftOps"
          width={28}
          height={28}
          className="app-sider-brand-logo"
        />
        {!opts.iconsOnly && (
          <>
            <Text strong className="app-sider-brand-name">
              SwiftOps
            </Text>
            {isOpsUser && <StatusChip tone="brand" label="Ops" size="sm" style={{ marginLeft: 4 }} />}
          </>
        )}
        {opts.showDesktopChrome && (
          <Button
            type="text"
            size="small"
            className="app-sider-toggle"
            icon={opts.iconsOnly ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleSiderIcons}
            aria-label={opts.iconsOnly ? t('shell.expandNav') : t('shell.collapseNav')}
            title={opts.iconsOnly ? t('shell.expandNav') : t('shell.collapseNav')}
          />
        )}
      </div>
      {orgSwitcher && !opts.iconsOnly && (
        <div style={{ padding: '0 12px 12px' }}>{orgSwitcher}</div>
      )}
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={opts.iconsOnly ? [] : openKeys}
        onOpenChange={opts.iconsOnly ? undefined : setOpenKeys}
        items={menuItems}
        onClick={({ key }) => navigate(key)}
        inlineIndent={MENU_INLINE_INDENT}
        inlineCollapsed={opts.iconsOnly}
        style={{ borderRight: 'none', fontSize: 'var(--t-hint)' }}
      />
    </>
  );

  /**
   * The seat bounce, rendered only now that every hook above has run on this
   * render exactly as it did on the last one. See `seatRedirect`'s note — the
   * early `return` this replaces was a white screen, not a redirect.
   */
  if (seatRedirect) return <Navigate to={seatRedirect} replace />;

  return (
    <Layout
      className={`app-layout${siderResizing ? ' app-layout--resizing' : ''}`}
      style={{ minHeight: '100vh', ['--sider-w' as string]: `${effectiveSiderW}px` }}
    >
      {!navCollapsed && (
        <>
          <Sider
            className={`app-sider${siderIconsOnly ? ' app-sider--icons' : ''}`}
            width={siderDragWidth ?? siderWidth}
            collapsed={siderIconsOnly}
            collapsedWidth={SIDER_COLLAPSED_W}
            theme="light"
            trigger={null}
          >
            {renderSidebar({ iconsOnly: siderIconsOnly, showDesktopChrome: true })}
          </Sider>
          <div
            className="app-sider-resize"
            role="separator"
            aria-orientation="vertical"
            aria-label={t('shell.resizeNav')}
            aria-valuenow={effectiveSiderW}
            aria-valuemin={SIDER_COLLAPSED_W}
            aria-valuemax={SIDER_MAX_W}
            onPointerDown={onSiderResizePointerDown}
          >
            <span className="app-sider-resize-grip" aria-hidden>
              <HolderOutlined />
            </span>
          </div>
        </>
      )}
      {navCollapsed && (
        <Drawer
          placement="left"
          width={260}
          className="app-sider"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          styles={{ body: { padding: 0 } }}
          closeIcon={null}
        >
          {renderSidebar({ iconsOnly: false, showDesktopChrome: false })}
        </Drawer>
      )}
      <Layout>
        {/* Email auto-login banner (spec 102, as-built amendment 2026-07-15) —
            the link session is now fully capable, so this is purely
            informational and staff-dismissible (per tab session). "Not you?
            Sign out" stays; the × only hides the bar, it does NOT log out. */}
        {user?.type === 'org' && user.amr === 'email_link' && !emailLinkBannerDismissed && (
          <div
            style={{
              background: '#fff7e6',
              borderBottom: '1px solid #ffe1b3',
              color: '#7a4d00',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              fontSize: 'var(--t-hint)',
              flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            <span>{t('auth:banner.emailLink', { email: user.email })}</span>
            <a
              onClick={handleLogout}
              style={{ cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', color: '#7a4d00' }}
            >
              {t('auth:banner.notYou')}
            </a>
            <button
              type="button"
              onClick={dismissEmailLinkBanner}
              aria-label={t('auth:banner.dismiss')}
              title={t('auth:banner.dismiss')}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#7a4d00',
                display: 'flex',
                alignItems: 'center',
                padding: 4,
                lineHeight: 0,
              }}
            >
              <CloseOutlined style={{ fontSize: 'var(--t-meta)' }} />
            </button>
          </div>
        )}
        {(
        <Header
          style={{
            background: '#fff',
            padding: isMobile ? '0 12px' : '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: collapseHeaderOnScroll ? 'none' : '1px solid #f0f0f0',
            gap: 12,
            // Dashboard auto-hide: collapse height + fade so it slides away on
            // scroll and the space is reclaimed by the content below.
            height: collapseHeaderOnScroll ? 0 : 64,
            overflow: 'hidden',
            opacity: collapseHeaderOnScroll ? 0 : 1,
            transition: 'height .25s ease, opacity .2s ease',
          }}
        >
          {navCollapsed && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileDrawerOpen(true)}
              style={{ marginRight: 'auto' }}
              aria-label={t('shell.openMenu')}
            />
          )}
          {opsOrgAccess && !isMobile && (
            <Alert
              type="info"
              showIcon
              message={t('shell.connectedTo', { name: opsOrgAccess.orgName, level: opsOrgAccess.level })}
              style={{ marginRight: 'auto', padding: '2px 12px' }}
              closable
              onClose={disconnectOrgAccess}
            />
          )}
          {/* Header bell removed (spec 110 D3) — the critical count lives on
              the My Queue nav badge + the Org-wide scope tab. */}
          {/* Spec 130 F4 — the unlabelled corner bubble was undiscoverable
              ("even I couldn't figure out there was an AI bot"), so Swift AI
              gets a named home in the header on every page. Opens the SAME
              PMChatWidget panel via the shared store — no second chat surface.
              Icon-only on mobile, where the header is tight. */}
          <Button
            className="ask-ai-btn"
            onClick={() => setPmChatOpen(true)}
            icon={<Sparkle size={14} filled />}
            aria-label={t('shell.askAi')}
            title={t('shell.askAiTooltip')}
          >
            {!isMobile && t('shell.askAi')}
          </Button>
          <Dropdown menu={userMenu} trigger={['click']}>
            <Space className="cursor-pointer">
              <Avatar
                style={
                  isOpsUser
                    ? { backgroundColor: 'var(--brand)' }
                    : { background: 'var(--gradient)' }
                }
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
              {!isMobile && (
                <div className="leading-tight">
                  <div className="text-(length:--t-body) leading-5 font-medium">{user?.firstName} {user?.lastName}</div>
                  <div className="text-(length:--t-meta) leading-4 text-gray-500">{subtitle}</div>
                </div>
              )}
            </Space>
          </Dropdown>
        </Header>
        )}
        <Content
          ref={contentRef}
          style={{
            // Sticky-bar routes zero the TOP padding so the page's own sticky bar
            // pins flush (see isStickyBarRoute above) — regardless of whether the
            // header is auto-hiding, so the bleed-through strip never appears on
            // narrow / hamburger screens.
            padding: isFullBleedRoute
              ? 0
              : isStickyBarRoute
                ? (isMobile ? '0 12px 12px' : '0 24px 24px')
                : isMobile ? 12 : 24,
            background: 'var(--bg)',
          }}
          onScroll={isAutoHideHeaderRoute ? onContentScroll : undefined}
        >
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Content>
      </Layout>
      <Suspense fallback={null}>
        <PMChatWidget />
      </Suspense>
      {/* Rides on top of whatever page loaded, instead of standing in front of
          it. Renders nothing unless the invite flow asked for it. */}
      <AppDownloadModal />
    </Layout>
  );
}
